import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';

import { commaEvery3rdChar, formatSeriesName } from '../../../../../../utils';


export default function FairValue({
  reportData,
  totalPageCount,
  exhibitNumber,
}) {
  const tableData = reportData.FairValuePerShare;

  const ownershipClasses = tableData.backsolve['Ownership Class'] || [];
  const sortedIndices = ownershipClasses
    .map((cls, idx) => ({ cls, idx }))
    .sort((a, b) => a.cls.localeCompare(b.cls))
    .map(obj => obj.idx);

  const backsolveSorted = {};
  Object.keys(tableData.backsolve).forEach(key => {
    backsolveSorted[key] = sortedIndices.map(i => tableData.backsolve[key][i]);
  });

  const pubcoSorted = tableData.pubco ? {} : null;
  if (tableData.pubco) {
    Object.keys(tableData.pubco).forEach(key => {
      pubcoSorted[key] = sortedIndices.map(i => tableData.pubco[key][i]);
    });
  }

  const concludedSorted = Array.isArray(tableData.concluded)
    ? sortedIndices.map((i) => tableData.concluded[i])
    : null;

  const backsolveWeight = parseFloat(String(reportData.weights?.backsolve ?? 0).replace('%', '')) || 0;
  const showBacksolveColumn = backsolveWeight > 0;

  const topHeaders = [
    '',
    ...showBacksolveColumn ? [`Weight ${`${reportData.weights.backsolve}%`}`] : [],
    ...tableData.pubco ? [`Weight ${`${reportData.weights.pubco}%`}`] : [],
    ...tableData.mna ? [`Weight ${`${reportData.weights.mna}%`}`] : [],
    'Concluded',
  ];

  const tableHeaders = [
    {
      header: 'Class',
      backendName: 'Ownership Class',
      formatting: ['series'],
    },
    {
      header: 'Shares',
      backendName: 'Shares',
      formatting: ['comma'],
      fix: 0,
    },
    {
      header: 'Aggregate\nvalue',
      backendName: 'Concluded Aggregate',
      formatting: ['comma', 'price'],
      fix: 0,
    },
    {
      header: 'Original\nissue price',
      backendName: 'Original Issue Price',
      formatting: ['price'],
      fix: 4,
    },
    ...showBacksolveColumn ? [{
      header: 'Backsolve fair value\n(per share)',
      backendName: 'Fair Value',
      formatting: ['price'],
      fix: 4,
    }] : [],
    ...tableData.pubco ? [
      {
        header: 'GPC fair value\n(per share)',
        backendName: 'Fair Value',
        formatting: ['price'],
        fix: 4,
      },
    ] : [],
    ...tableData.mna ? [
      {
        header: 'Backsolve fair value\n(per share)',
        backendName: 'Fair Value',
        formatting: ['price'],
        fix: 4,
      },
    ] : [],
    {
      header: 'Weighted fair value\n(per share)',
      backendName: 'Fair Value',
      formatting: ['price'],
      fix: 4,
    },
  ];

  const formatters = {
    comma: (val) => commaEvery3rdChar(val),
    price: (val) => `$${val}`,
    percent: (val) => `${val}%`,
    series: (val) => formatSeriesName(val),
  };

  function concludedPerShare(rowIndex) {
    if (concludedSorted && concludedSorted[rowIndex] != null) return Number(concludedSorted[rowIndex]);
    if (tableData.pubco && pubcoSorted?.['Fair Value']?.[rowIndex] != null) {
      return (
        Number(backsolveSorted['Fair Value'][rowIndex] || 0) * (backsolveWeight / 100) +
        Number(pubcoSorted['Fair Value'][rowIndex] || 0) * ((parseFloat(String(reportData.weights?.pubco ?? 0).replace('%', '')) || 0) / 100)
      );
    }
    return Number(backsolveSorted['Fair Value'][rowIndex] || 0);
  }

  function returnCellData(tableHeader, rowIndex) {
    let dataToReturn;
    if (tableHeader.header === 'Aggregate\nvalue') {
      const shares = Number(backsolveSorted.Shares[rowIndex] || 0);
      dataToReturn = concludedPerShare(rowIndex) * shares;
    } else if (['GPC fair value\n(per share)', 'Weighted fair value\n(per share)', 'DLOM*'].includes(tableHeader.header)) {
      if (tableHeader.header === 'GPC fair value\n(per share)') dataToReturn = pubcoSorted?.[tableHeader.backendName][rowIndex];
      if (tableHeader.header === 'Weighted fair value\n(per share)') dataToReturn = concludedPerShare(rowIndex);
    } else if (tableHeader.header === 'Original\nissue price') {
      const className = backsolveSorted['Ownership Class'][rowIndex] || '';
      const raw = backsolveSorted['Original Issue Price'][rowIndex];
      if (/^common$/i.test(String(className).trim()) && (!raw || Number(raw) === 0)) {
        return 'N/A';
      }
      dataToReturn = raw;
    } else {
      dataToReturn = backsolveSorted[tableHeader.backendName][rowIndex];
    }
    if (dataToReturn == null || dataToReturn === '') return 'N/A';
    if (tableHeader.fix !== undefined) dataToReturn = Number(dataToReturn).toFixed(tableHeader.fix);
    tableHeader.formatting.forEach((formatter) => { dataToReturn = formatters[formatter](dataToReturn); });
    return dataToReturn;
  }

  function returnTotal(tableHeader) {
    if (tableHeader.header === 'Class') return 'Total';
    if (tableHeader.header === 'Shares') {
      let total = backsolveSorted.Shares.reduce((a, b) => a + Number(b || 0), 0);
      total = total.toFixed(0);
      tableHeader.formatting.forEach((formatter) => { total = formatters[formatter](total); });
      return total;
    }
    if (tableHeader.header === 'Aggregate\nvalue') {
      let total = backsolveSorted.Shares.reduce((sum, shares, idx) => (
        sum + (Number(shares || 0) * concludedPerShare(idx))
      ), 0);
      total = total.toFixed(0);
      tableHeader.formatting.forEach((formatter) => { total = formatters[formatter](total); });
      return total;
    }
    return '';
  }

  const topHeaderWidth = (index) => {
    if (index === 0) return '444px';
    if (topHeaders.length === 5) return '128px';
    if (topHeaders.length === 4 && [1, 2].includes(index)) return '192px';
    if (topHeaders.length === 3 && index === 1) return '144px';
    if (topHeaders.length === 3 && index === 2) return '432px';
    return '128px';
  };

  const columnWidth = (index) => {
    if (index === 0) return '156px';
    if ([1, 2, 3].includes(index)) return '96px';
    if (topHeaders.length === 5) return '144px';
    if (topHeaders.length === 4 && [4, 5, 6].includes(index)) return '160px';
    if (topHeaders.length === 3 && index === 4) return '144px';
    if (topHeaders.length === 3 && index === 5) return '432px';
    return '144px';
  };

  const footnote = reportData.footnoteData.fairValuePerShare;

  return (
    <Page
      size="TABLOID"
      orientation="landscape"
      style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
    >
      <TablePageHeader
        companyName={reportData.companyInfo.companyName}
        pageName={`Fair Value Per Share   |   Exhibit ${exhibitNumber}`}
      />
      <View
        style={{
          marginTop: '84px',
          marginLeft: '80px',
          display: 'flex',
          flexDirection: 'column',
          width: '1064px',
          border: '1px',
          borderColor: '#AFB1B3',
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            paddingTop: '12px',
            paddingLeft: '16px',
            height: '56px',
            backgroundColor: '#0C2617',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '10px',
              color: '#E1E2EC',
              letterSpacing: '0.08px',
            }}
          >
            Fair value per share conclusion
          </Text>
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '10px',
              color: '#A4ABC0',
              letterSpacing: '0.08px',
            }}
          >
            Summary of results
          </Text>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '32px',
            backgroundColor: '#307157',
          }}
        >
          {topHeaders.map((dataName, index) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '10px',
                color: '#E1E2EC',
                width: topHeaderWidth(index),
                marginTop: 'auto',
                marginRight: '16px',
                paddingBottom: '6px',
                letterSpacing: '0.5px',
                textAlign: 'center',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {dataName}
            </Text>
          ))}
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '56px',
            backgroundColor: '#307157',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          {tableHeaders.map((dataName, index) => (
            <Text
              key={dataName.header}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '10px',
                color: '#E1E2EC',
                width: columnWidth(index),
                marginTop: 'auto',
                ...index === 0 && { paddingLeft: '16px' },
                letterSpacing: '0.5px',
                textAlign: index !== 0 && 'center',
                lineHeight: '0.68mm',
                ...index === 3 && { marginRight: 'auto' },
                ...index === 4 && { marginRight: '42px' },
              }}
            >
              {dataName.header}
            </Text>
          ))}
        </View>
        {tableData.backsolve['Ownership Class'].map((equityClass, rowIndex) => (
          <View
            key={equityClass}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '26px',
              backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
            }}
          >
            {tableHeaders.map((tableHeader, cellIndex) => (
              <Text
                key={tableHeader.header}
                style={{
                  fontFamily: 'Roboto',
                  fontSize: '10px',
                  color: '#49454F',
                  width: columnWidth(cellIndex),
                  ...cellIndex === 0 && { paddingLeft: '16px' },
                  letterSpacing: '0.4px',
                  ...cellIndex !== 0 && { textAlign: 'center' },
                  ...cellIndex === 3 && { marginRight: 'auto' },
                  ...cellIndex === 4 && { marginRight: '42px' },
                }}
              >
                {returnCellData(tableHeader, rowIndex) || 'N/A'}
              </Text>
            ))}
          </View>
        ))}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '32px',
            backgroundColor: '#9ECEBB',
          }}
        >
          {tableHeaders.map((tableHeader, cellIndex) => (
            <Text
              key={tableHeader.header}
              style={{
                fontFamily: 'Roboto',
                fontSize: '10px',
                color: '#49454F',
                width: columnWidth(cellIndex),
                ...cellIndex === 0 && { paddingLeft: '16px' },
                letterSpacing: '0.4px',
                ...cellIndex !== 0 && { textAlign: 'center' },
              }}
            >
              {returnTotal(tableHeader)}
            </Text>
          ))}
        </View>
      </View>
      {!!footnote && (
        <Text
          style={{
            fontFamily: 'Roboto',
            fontSize: '8px',
            color: '#49454F',
            letterSpacing: '0.4px',
            marginTop: '16px',
            marginLeft: '90px',
            width: '450px',
            lineHeight: '0.55mm',
          }}
        >
          Footnote:&nbsp;
          {footnote}
        </Text>
      )}
      <BottomPagination
        pageNumber={totalPageCount}
        reportData={reportData}
      />
      {reportData.isDraft && <DraftOverlay />}
    </Page>
  );
}

FairValue.propTypes = {
  reportData: PropTypes.object.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
};
